import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, ChevronRight, Send } from 'lucide-react';
import WedifyApiRequest from '../../../utils/wedify_api_request.js';
import { getAuth } from 'firebase/auth';

interface Message {
  id: string;
  vendorName: string;
  vendorType: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  vendorImage?: string;
  status?: string;
}

interface ChatMessage {
  id: string;
  sender: 'user' | 'vendor';
  message: string;
  timestamp: string;
}

interface MessagesSectionProps {
  onMessageClick: (message: Message) => void;
}

const MessagesSection: React.FC<MessagesSectionProps> = ({ onMessageClick }) => {
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    const getSentEmails = async () => {
      const wedifyApiRequest = new WedifyApiRequest();
      const userId = getAuth().currentUser?.uid;
      const userInfo = await wedifyApiRequest.makeRequest(
        `${process.env.REACT_APP_API_BASE_URL}/api/login/users/get/external/id?authService=firebase&externalClientId=${userId}`,
        "GET",
      );
      const userInfoJson = await userInfo?.json();
      const headers = {
        'userId': userInfoJson?.id,
        'userType': 'COUPLE',
      }
      const sentEmails = await wedifyApiRequest.makeRequest(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/resend/sent-by`,
        "GET",
        null,
        headers
      );
      const sentEmailsJson = await sentEmails?.json();
      
      // Process the messages to extract the actual message content from bodyText
      const processedMessages = await Promise.all(sentEmailsJson.map(async (email: any) => {
        // Extract message content from bodyText
        const messageMatch = email.bodyText?.match(/Message:\s*([^\n]+)/);
        const extractedMessage = messageMatch ? messageMatch[1].trim() : email.bodyText || '';
        
        // Fetch vendor information using ToUserId
        try {
          const vendorResponse = await wedifyApiRequest.makeRequest(
            `${process.env.REACT_APP_API_BASE_URL}/api/vendors/${email.toUserId}`,
            "GET",
          );
          const vendorData = await vendorResponse?.json();
          
          return {
            ...email,
            message: extractedMessage,
            vendorType: vendorData.vendorType // Set vendorType from vendorData
          };
        } catch (error) {
          console.error('Error fetching vendor data:', error);
          return {
            ...email,
            message: extractedMessage,
            vendorType: 'Unknown' // Fallback vendorType if fetch fails
          };
        }
      }));
      
      setMessages(processedMessages);
    };
    getSentEmails();
  }, []);
  // Update the filtering logic
  const unreadMessages = messages.filter(msg => msg.status === "email.sent" || msg.status === "email.delivered");
  const readMessages = messages.filter(msg => msg.status === "email.opened" || msg.status === "email.clicked");

  const handleMessageClick = (message: Message) => {
    setExpandedMessageId(expandedMessageId === message.id ? null : message.id);
  };

  const handleSendMessage = (messageId: string) => {
    if (!newMessage.trim()) return;
    // Here you would typically send the message to your backend
    setNewMessage('');
  };

  const renderMessageCard = (message: Message, isUnread: boolean) => {
    const isExpanded = expandedMessageId === message.id;
    const chatHistory: ChatMessage[] = [
      {
        id: '1',
        sender: 'user',
        message: message.message,
        timestamp: message.timestamp
      },
      {
        id: '2',
        sender: 'vendor',
        message: 'Of course! We offer a wide range of services including full-day coverage, engagement shoots, and custom packages. What specific aspects are you interested in?',
        timestamp: '45m ago'
      }
    ];

    return (
      <div
        key={message.id}
        className={`w-full rounded-lg border transition-all duration-200 ${
          isUnread
            ? 'bg-white border-[#FF4E8E]/20 hover:border-[#FF4E8E]/30'
            : 'bg-white border-gray-200 hover:border-gray-300'
        }`}
      >
        {/* Message Header */}
        <button
          onClick={() => handleMessageClick(message)}
          className="w-full p-3 sm:p-4 flex items-start gap-2 sm:gap-3 group"
        >
          {/* Vendor Image */}
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden border border-gray-200 group-hover:border-gray-300 transition-colors flex-shrink-0">
            <img
              src={message.vendorImage || '/themesOption/placeholder.jpg'}
              alt={message.vendorName}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Message Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm sm:text-base font-medium truncate group-hover:text-[#FF4E8E] transition-colors">{message.vendorName}</h3>
              <span className="text-xs text-gray-500 ml-2">{message.timestamp}</span>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 mb-1 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600 group-hover:bg-gray-200 transition-colors">
                {message.vendorType}
              </span>
              {isUnread && (
                <span className="px-2 py-0.5 rounded-full bg-[#FF4E8E]/10 text-xs text-[#FF4E8E]">
                  New
                </span>
              )}
            </div>
            
            <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 group-hover:text-gray-900 transition-colors">{message.message}</p>
          </div>
          
          {/* Status and Arrow */}
          <div className="flex-shrink-0 flex flex-col items-end gap-1 sm:gap-2">
            <ChevronRight 
              className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-400 group-hover:text-[#FF4E8E] transition-colors ${
                isExpanded ? 'rotate-90' : ''
              }`} 
            />
          </div>
        </button>

        {/* Expanded Chat History */}
        {isExpanded && (
          <div className="border-t border-gray-200">
            <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-h-[250px] sm:max-h-[300px] overflow-y-auto">
              {chatHistory.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[80%] rounded-lg p-2 sm:p-3 ${
                      msg.sender === 'user'
                        ? 'bg-[#FF4E8E] text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-xs sm:text-sm">{msg.message}</p>
                    <span className="text-[10px] sm:text-xs mt-1 block opacity-70">
                      {msg.timestamp}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="p-3 sm:p-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(message.id)}
                  placeholder="Type your message..."
                  className="flex-1 p-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#FF4E8E] focus:ring-1 focus:ring-[#FF4E8E] text-sm"
                />
                <button
                  onClick={() => handleSendMessage(message.id)}
                  className="p-2 rounded-lg bg-[#FF4E8E] text-white hover:bg-[#FF4E8E]/90 transition-colors"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-200px)] overflow-auto pb-safe">
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500">
          <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 mb-3 opacity-50" />
          <p className="text-sm font-medium">No messages yet</p>
          <p className="text-xs mt-1 text-gray-400">Your inquiries will appear here</p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {/* Unread Messages Section */}
          {unreadMessages.length > 0 && (
            <div>
              <div className="px-3 sm:px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="text-xs font-medium text-gray-500">UNREAD BY VENDOR</h3>
              </div>
              <div className="space-y-2 p-2">
                {unreadMessages.map(message => renderMessageCard(message, true))}
              </div>
            </div>
          )}

          {/* Read Messages Section */}
          {readMessages.length > 0 && (
            <div>
              <div className="px-3 sm:px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="text-xs font-medium text-gray-500">READ BY VENDOR</h3>
              </div>
              <div className="space-y-2 p-2">
                {readMessages.map(message => renderMessageCard(message, false))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesSection; 